import { CellContext, ColumnDef } from '@tanstack/react-table';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import { localFormat } from 'helpers/date-helpers';
import { useMemo } from 'react';
import { TemplateVersionHistoryItemDto } from 'services/api/api-client.types';
import styles from './VersionHistoryModal.module.scss';
import { AppMenuItem } from 'components/uikit/menu/AppMenu';
import { MenuDirection } from 'components/uikit/menu/MenuDirection';
import { DotMenu } from 'components/uikit/menu/DotMenu';
import { Links } from 'application/constants/links';
import { useNavigate } from 'react-router';
import {
  createProjectDocument,
  downloadTemplate,
} from 'helpers/documents/fileHandler';
import { QueryFactory } from 'services/api';
import { replaceItemById } from 'helpers/documents/documentsObserver';
import { Comment } from './Comment';

export const useVersionHistoryTableColumns = (props: {
  setIsLoading: (isLoading: boolean) => void;
  onVersionDelete: () => Promise<void>;
  secondToLastVersionId?: number;
}) => {
  const i18n = useScopedTranslation('Page.Templates.VersionHistoryModal');
  const i18nDeleteReasons = useScopedTranslation(
    'Page.Templates.DeleteDisableReasons',
  );
  const navigate = useNavigate();

  return useMemo<ColumnDef<TemplateVersionHistoryItemDto>[]>(
    () => [
      {
        accessorKey: 'name',
        header: i18n.t('Columns.name'),
        cell: ({ row }: CellContext<TemplateVersionHistoryItemDto, any>) => (
          <div className={styles.nameColumn}>{row.original.name}</div>
        ),
        enableSorting: false,
      },
      {
        accessorKey: 'comment',
        header: i18n.t('Columns.comment'),
        cell: ({ row }: CellContext<TemplateVersionHistoryItemDto, any>) => (
          <Comment text={row.original.comment} templateId={row.original.id} />
        ),
        enableSorting: false,
      },
      {
        accessorKey: 'uploadedBy',
        header: i18n.t('Columns.uploadedBy'),
        cell: ({ row }: CellContext<TemplateVersionHistoryItemDto, any>) => (
          <div className={styles.uploadedByColumn}>
            {row.original.auditData?.created?.byName}
          </div>
        ),
        enableSorting: false,
      },
      {
        accessorKey: 'uploadDate',
        header: i18n.t('Columns.uploadDate'),
        cell: ({ row }: CellContext<TemplateVersionHistoryItemDto, any>) => (
          <div>
            {localFormat(row.original.auditData?.created?.date!, 'dd.MM.yyyy')}
          </div>
        ),
        enableSorting: false,
      },
      {
        accessorKey: 'menu',
        header: '',
        cell: ({ row }: CellContext<TemplateVersionHistoryItemDto, any>) => {
          const getDeleteDisableReason = (): string | undefined => {
            const isCurrentVersion = row.index === 0;
            if (!isCurrentVersion)
              return i18nDeleteReasons.t('notLatestVersion');

            const isOnlyVersion = !props.secondToLastVersionId;
            if (isOnlyVersion) return i18nDeleteReasons.t('onlyVersion');

            if (row.original.hasDerivedDocuments)
              return i18nDeleteReasons.t('hasDerivedDocuments');
          };
          const deleteDisableReason: string | undefined =
            getDeleteDisableReason();
          const isDeleteDisabled = !!deleteDisableReason;

          const menuItems: AppMenuItem[] = [
            {
              key: 'view',
              text: i18n.t('ContextMenu.view'),
              className: styles.menuItem,
              onClick: () => {
                navigate(
                  Links.Authorized.OpenTemplate.link({
                    templateId: row.original.id,
                  }),
                );
              },
            },
            {
              key: 'download',
              text: i18n.t('ContextMenu.download'),
              className: styles.separatorMenuItem,
              onClick: async () => {
                props.setIsLoading(true);
                await downloadTemplate(row.original.id);
                props.setIsLoading(false);
              },
            },
            {
              key: 'delete',
              text: i18n.t('ContextMenu.delete'),
              className: styles.delete,
              disabled: isDeleteDisabled,
              tooltipMessage: deleteDisableReason,
              onClick: async () => {
                props.setIsLoading(true);
                await QueryFactory.TemplateClient.delete_(row.original.id);

                let folders = await QueryFactory.TemplateClient.getFolders();

                const document = await QueryFactory.TemplateClient.get(
                  props.secondToLastVersionId!,
                );
                const documentNode = createProjectDocument(
                  String(document.initialId ?? document.id),
                  document.name,
                  document.id,
                );

                folders = replaceItemById(
                  String(document.initialId),
                  folders as any,
                  documentNode as any,
                );

                await QueryFactory.TemplateClient.patchFolders({
                  folders,
                });
                await props.onVersionDelete();
                props.setIsLoading(false);
              },
            },
          ];

          return (
            <DotMenu
              menuItems={menuItems}
              dotMenuClassName={styles.dotMenu}
              direction={MenuDirection.bottomRightEdge}
            />
          );
        },
        enableSorting: false,
      },
    ],
    [i18n, props.secondToLastVersionId],
  );
};
